import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Artwork } from '@/types/entities'
import type { UpdateArtworkData } from '../api/artworkApi'
import { TagSelect } from './TagSelect'
import { TagList } from './TagList'
import { useTags } from '../hooks/useTags'
import { Info } from 'lucide-react'

const artworkSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  tags: z.array(z.number()).max(5, 'Maximum 5 tags allowed'),
  allowWholesale: z.boolean(),
  matureContent: z.boolean(),
})

type ArtworkFormData = z.infer<typeof artworkSchema>

interface BasicInfoFormProps {
  artwork: Artwork
  readOnly?: boolean
  onSave: (data: UpdateArtworkData) => void
}

export function BasicInfoForm({ artwork, readOnly = false, onSave }: BasicInfoFormProps) {
  const { data: tags } = useTags()

  const form = useForm<ArtworkFormData>({
    resolver: zodResolver(artworkSchema),
    defaultValues: {
      name: artwork.name,
      // Artworks are uploaded without a description, so it arrives as null. Loading null into a
      // field the schema types as a string failed validation on every Save — the artist saw an
      // error under Description however they edited the title.
      description: artwork.description ?? '',
      // The form holds tag ids; the artwork arrives with tag objects. Loading the objects as
      // they were made the form fail `z.array(z.number())` for any artwork that already had a
      // tag, so Save silently did nothing and a picked tag never appeared.
      tags: (artwork.tags || []).map((t) => (typeof t === 'number' ? t : t.id)),
      allowWholesale: artwork.allowWholesale ?? true,
      matureContent: artwork.matureContent ?? false,
    },
  })

  const handleSubmit = (data: ArtworkFormData) => {
    onSave(data)
  }

  return (
    <Form {...form}>
      <form id="artwork-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input {...field} disabled={readOnly} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  disabled={readOnly}
                  rows={4}
                  placeholder="What inspired it? Is there a story behind it?"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </FormControl>
              <FormDescription>
                {field.value?.length || 0} characters
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {!readOnly && tags && (
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>
                <FormControl>
                  <TagSelect
                    value={field.value}
                    onChange={field.onChange}
                    tags={tags}
                    maxTags={5}
                  />
                </FormControl>
                <FormDescription>Select up to 5 tags for your artwork</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* What the form will save, so a picked or removed tag shows straight away. */}
        <TagList
          tags={form.watch('tags')}
          readOnly={readOnly}
          onRemove={(tagId) =>
            form.setValue(
              'tags',
              form.getValues('tags').filter((id) => id !== tagId),
              { shouldDirty: true }
            )
          }
        />

        <FormField
          control={form.control}
          name="allowWholesale"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={readOnly}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <div className="flex items-center gap-2">
                  <FormLabel>Allow wholesale</FormLabel>
                  <button
                    type="button"
                    title="Tick this box to make your art eligible to be sold across our partner network of retailers worldwide. We highly recommend it, as it's a great way to boost sales."
                  >
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="matureContent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={readOnly}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Mature content</FormLabel>
              </div>
            </FormItem>
          )}
        />
      </form>
    </Form>
  )
}

